import { Archive, Print } from "@mui/icons-material";
import {
    Button,
    CircularProgress,
    Drawer,
    IconButton,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../AppContext";
import { useLoadDoctype } from "../../shared/hooks/useFrappeDoctype";
import { useClosedInvoices } from "../../shared/hooks/useInvoicesData";
import { useGetDocTypeList } from "../../shared/hooks/useFrappeDoctype";
import {
    useGetAllTables,
    usePrintOrder,
} from "../../shared/hooks/useTablesData";
import { PosContext } from "../../shared/PosContext";
import { dateTimeFormat } from "../../utils/dateUtils";
import { getOrderShortName } from "../../utils/floorUtils";
import SimpleWidgetsHeader from "../SimpleWidgetsHeader/SimpleWidgetsHeader";
import Spinner from "../Spinner";
import { useStyles } from "./styles";
import ClosedOrderItemRow from "./ClosedOrderItemRow";

function ClosedOrders({ open, onClose }) {
    const classes = useStyles();
    const { settingsInvoicePrinter } = useContext(AppContext);
    const { getMenuItem, addonsItems } = useContext(PosContext);

    const { data: allTables } = useGetAllTables();
    const printOrderMutation = usePrintOrder();

    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [printingOrder, setPrintingOrder] = useState(null);
    const [mode, setMode] = useState("today"); // today | all
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const { data: invoiceData } = useLoadDoctype(
        "POS Invoice",
        selectedInvoice
    );

    const { data: tableOrderRows } = useGetDocTypeList({
        doctype: selectedInvoice ? "Table Order" : null,
        filters: selectedInvoice ? [["link_invoice", "=", selectedInvoice]] : null,
        fields: ["name", "table_description", "customer", "customer_name", "total", "amount", "notes", "creation"],
        sortBy: "modified desc",
        limit: 1,
    });

    const tableOrderName = tableOrderRows && tableOrderRows.length ? tableOrderRows[0].name : null;
    const { data: tableOrderData } = useLoadDoctype("Table Order", tableOrderName);
    const tableOrderDoc = tableOrderData?.docs?.[0] || null;

    // Debounce search to avoid spamming queries
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText), 300);
        return () => clearTimeout(t);
    }, [searchText]);

    const closedInvoicesQuery = useClosedInvoices({
        mode,
        limit: 60,
        search: debouncedSearch,
        enabled: open,
    });

    // Derive list rows without storing derived state (prevents render loops from unstable deps like `.flat()`).
    const orders = useMemo(() => {
        const closedInvoices = (closedInvoicesQuery.data?.pages || []).flat();
        if (!closedInvoices || !allTables) return [];

        const returnedInvoices = closedInvoices
            .filter((i) => i.status === "Return")
            .map((i) => i.return_against);

        const paidNonReturned = closedInvoices.filter(
            (i) =>
                !i.return_against &&
                i.status === "Paid" &&
                !returnedInvoices.includes(i.name)
        );

        return paidNonReturned.map((i) => {
            const _invoice = { ...i };
            const suffix = _invoice.customer_name?.split("-")?.slice(-1)?.[0];
            const _table =
                allTables.find((t) => t.name === suffix) || {
                    description: "takeaway",
                };
            return { ..._invoice, table: _table.description };
        });
    }, [closedInvoicesQuery.data?.pages, allTables]);

    const handlePrint = (order) => {
        if (!settingsInvoicePrinter) {
            setPrintingOrder(order.name);
            
            const printUrl = `/api/method/frappe.utils.print_format.download_pdf?doctype=POS%20Invoice&name=${order.name}&format=POS&no_letterhead=0&settings=%7B%7D&_lang=en`;
            // const printUrl = `/api/method/frappe.utils.print_format.download_pdf?doctype=POS%20Invoice&name=${order.name}&format=POS%20Invoice&no_letterhead=0&letterhead=Antico&settings=%7B%7D&_lang=en`;

            // Create a hidden iframe to handle the PDF printing
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = printUrl;
            document.body.appendChild(iframe);

            // Wait for the iframe to load and then print
            iframe.onload = () => {
                try {
                    iframe.contentWindow.print();
                } catch (error) {
                    console.error(
                        "Error printing invoice:",
                        error
                    );
                }
                // Clear the loading state
                setPrintingOrder(null);
                // Clean up the iframe after printing
                // setTimeout(() => {
                // document.body.removeChild(iframe);
                // }, 1000);
            };
        } else {
            printOrderMutation.mutate(
                {
                    orderId: order.name,
                    doctype: "POS Invoice",
                },
                {
                    onSuccess: () => {
                        console.log("printed");
                    },
                    onError: (err) => {
                        console.log(err);
                    },
                }
            );
        }
    };

    const invoiceDoc = invoiceData?.docs?.[0] || null;

    const invoiceDerived = useMemo(() => {
        // Prefer Table Order (it has parent_item + identifier semantics we need)
        const isLinkingToTableOrder = !!selectedInvoice;
        const sourceDoc = tableOrderDoc || null;

        // If we have a selected invoice but Table Order isn't loaded yet, avoid falling back
        // to POS Invoice custom_order_items (which can make children appear as top-level).
        if (isLinkingToTableOrder && !sourceDoc) {
            return {
                parentEntries: [],
                total: 0,
                table: "",
                notes: "",
                customer: "",
                customerName: "",
                totalQty: 0,
                timeLabel: "",
                isLoadingItems: true,
            };
        }

        if (!sourceDoc) {
            return {
                parentEntries: [],
                total: 0,
                table: "",
                notes: "",
                customer: "",
                customerName: "",
                totalQty: 0,
                timeLabel: "",
                isLoadingItems: false,
            };
        }

        const items = sourceDoc.entry_items || [];
        let total = 0;
        items.forEach((it) => {
            total += (it.qty || 0) * (it.rate || 0);
        });

        // Build parent/addon grouping (Model A) using parent_item + identifier, if present.
        const normalizeNull = (v) => {
            if (v === null || v === undefined) return "";
            let s = String(v).trim();
            if (!s) return "";
            // Strip wrapping quotes (e.g. "\"NULL\"")
            if (
                (s.startsWith('"') && s.endsWith('"')) ||
                (s.startsWith("'") && s.endsWith("'"))
            ) {
                s = s.slice(1, -1).trim();
            }
            const lower = s.toLowerCase();
            if (lower === "null" || lower === "none") return "";
            return s;
        };

        const withIds = items.map((row, idx) => {
            const identifier =
                normalizeNull(row.identifier) ||
                normalizeNull(row.name) ||
                `${row.item_code}-${idx}`;
            const parent_item = normalizeNull(row.parent_item);
            return { ...row, __identifier: identifier, __parent_item: parent_item };
        });

        const childrenByParent = new Map();
        const parents = [];

        withIds.forEach((row) => {
            if (row.__parent_item) {
                const list = childrenByParent.get(row.__parent_item) || [];
                list.push(row);
                childrenByParent.set(row.__parent_item, list);
            } else {
                parents.push(row);
            }
        });

        const buildDisplay = (row) => {
            const menuItem = getMenuItem?.(row.item_code);
            const cover = menuItem?.cover || null;
            const item_name = row.item_name || menuItem?.item_name || row.item_code;
            const notes = row.notes || row.custom_notes || row.item_notes || "";
            return {
                id: row.__identifier,
                item_code: row.item_code,
                item_name,
                qty: row.qty || 0,
                rate: row.rate || 0,
                amount: (row.qty || 0) * (row.rate || 0),
                cover,
                notes,
            };
        };

        const parentEntries = parents
            .map((p) => {
                const display = buildDisplay(p);
                const childRows = childrenByParent.get(p.__identifier) || [];
                const addonEntriesModelA = childRows.map((c) => buildDisplay(c));

                // Model B fallback: `addons` is JSON list of item_code strings
                let addonEntriesModelB = [];
                if (addonEntriesModelA.length === 0) {
                    // In Table Order, `addons` is often a JSON array of objects (item_code, rate, qty...)
                    const raw = p.addons ? JSON.parse(p.addons) : [];
                    if (Array.isArray(raw) && raw.length > 0) {
                        addonEntriesModelB = raw
                            .map((a, idx) => {
                                const code = a?.item_code || a?.item || a?.name;
                                if (!code) return null;
                                const meta = getMenuItem?.(code) || addonsItems?.find((x) => x.item_code === code);
                                const rate = Number(a?.standard_rate ?? a?.rate ?? meta?.standard_rate ?? 0);
                                const qty = Number(a?.qty ?? 1);
                                return {
                                    id: `addon:${display.id}:${code}:${idx}`,
                                    item_code: code,
                                    item_name: a?.item_name || meta?.item_name || code,
                                    qty,
                                    rate,
                                    amount: qty * rate,
                                    cover: meta?.cover || meta?.image || null,
                                    notes: a?.notes || "",
                                };
                            })
                            .filter(Boolean);
                    }
                }

                return {
                    ...display,
                    addons: addonEntriesModelA.length ? addonEntriesModelA : addonEntriesModelB,
                };
            })
            // Safety: ensure we never render child rows as top-level even if data is weird
            .filter((p) => !!p.item_code);

        const table = sourceDoc.table_description || "takeaway";

        const notes =
            sourceDoc.notes ||
            sourceDoc.custom_notes ||
            sourceDoc.custom_order_notes ||
            sourceDoc.remarks ||
            "";

        const customerName = sourceDoc.customer_name || "";
        const customer = sourceDoc.customer || "";
        const totalQty = sourceDoc.total_qty || 0;

        const timeLabel = sourceDoc.creation ? dateTimeFormat(sourceDoc.creation) : "";

        return {
            parentEntries,
            total,
            table,
            notes,
            customer,
            customerName,
            totalQty,
            timeLabel,
            isLoadingItems: false,
        };
    }, [selectedInvoice, tableOrderDoc, getMenuItem, addonsItems]);

    return (
        <React.Fragment>
            <Drawer
                className={classes.Drawer}
                anchor="right"
                open={open}
                onClose={onClose}
            >
                <div className={classes.DrawerWrapper}>
                    <SimpleWidgetsHeader
                        onClose={onClose}
                        Icon={Archive}
                        title="Closed Orders"
                        // action={newTakeawayOrder}
                    />

                    <div style={{ padding: "0 1rem 0.75rem 1rem" }}>
                        <div
                            style={{
                                display: "flex",
                                gap: "0.75rem",
                                alignItems: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            <ToggleButtonGroup
                                size="small"
                                value={mode}
                                exclusive
                                onChange={(_, v) => {
                                    if (!v) return;
                                    setMode(v);
                                    // reset selection + list while switching
                                    setSelectedInvoice(null);
                                }}
                            >
                                <ToggleButton value="today">Today</ToggleButton>
                                <ToggleButton value="all">All</ToggleButton>
                            </ToggleButtonGroup>

                            <TextField
                                size="small"
                                placeholder="Search orders…"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ flex: 1, minWidth: 180 }}
                            />
                        </div>
                    </div>

                    <div className={classes.OrderItems}>
                        <div className="Orders__List">
                            <div className="Orders__List_RowWrapper">
                                <div className="Orders__List_header">
                                    <div>Table</div>
                                    <div>Order</div>
                                    <div>Total</div>
                                    <div>Time</div>
                                    <div>Total Items</div>
                                </div>
                                <span className="Orders__List_Action">
                                    <IconButton
                                        style={{ visibility: "hidden" }}
                                    >
                                        <Print />
                                    </IconButton>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={classes.OrderItems}>
                        <div className="Orders__List  md">
                            {closedInvoicesQuery.isLoading ? (
                                <div style={{ padding: "1rem" }}>
                                    <Spinner />
                                </div>
                            ) : null}
                            {orders.map((order, idx) => {
                                return (
                                    <div
                                        key={`order-${idx}`}
                                        className="Orders__List_RowWrapper"
                                    >
                                        <div
                                            className="Orders__List_item"
                                            onClick={() =>
                                                setSelectedInvoice(order.name)
                                            }
                                        >
                                            <div>{order.table}</div>
                                            <div>
                                                {getOrderShortName(order.name)}
                                            </div>
                                            <div>{order.total}</div>
                                            <div>
                                                {dateTimeFormat(
                                                    order.posting_time
                                                )}
                                            </div>
                                            <div>{order.total_qty}</div>
                                        </div>
                                        <span className="Orders__List_Action">
                                            <IconButton
                                                onClick={() =>
                                                    handlePrint(order)
                                                }
                                                disabled={printingOrder === order.name}
                                            >
                                                {printingOrder === order.name ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <Print />
                                                )}
                                            </IconButton>
                                        </span>
                                    </div>
                                );
                            })}

                            {mode === "all" ? (
                                <div style={{ padding: "0.75rem 1rem" }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        disabled={
                                            closedInvoicesQuery.isFetchingNextPage ||
                                            !closedInvoicesQuery.hasNextPage
                                        }
                                        onClick={() =>
                                            closedInvoicesQuery.fetchNextPage()
                                        }
                                    >
                                        {closedInvoicesQuery.isFetchingNextPage
                                            ? "Loading…"
                                            : closedInvoicesQuery.hasNextPage
                                            ? "Load more"
                                            : "No more"}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Drawer>

            {/* Details drawer (keeps list drawer open) */}
            <Drawer
                anchor="right"
                open={!!selectedInvoice}
                onClose={() => setSelectedInvoice(null)}
                PaperProps={{
                    style: {
                        width: 520,
                        maxWidth: "100%",
                    },
                }}
            >
                <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <div
                        style={{
                            padding: "0.75rem 1rem",
                            borderBottom: "1px solid #e5e9f2",
                            background: "#f5f6fa",
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.75rem",
                        }}
                    >
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 900 }}>
                                {selectedInvoice ? getOrderShortName(selectedInvoice) : "Order"}
                            </div>
                            <div style={{ color: "#6E7187", fontSize: 12 }}>
                                {invoiceDerived.timeLabel}
                            </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                            <IconButton
                                onClick={() => {
                                    if (invoiceDoc?.name) handlePrint(invoiceDoc);
                                }}
                                disabled={!invoiceData || printingOrder === selectedInvoice}
                            >
                                {printingOrder === selectedInvoice ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Print />
                                )}
                            </IconButton>
                            <Button onClick={() => setSelectedInvoice(null)}>Close</Button>
                        </div>
                    </div>

                    <div style={{ flex: 1, overflow: "auto", padding: "1rem" }}>
                        {!!invoiceDoc ? (
                            <InvoiceDetails
                                classes={classes}
                                parentEntries={invoiceDerived.parentEntries}
                                total={invoiceDerived.total}
                                summary={{
                                    table: invoiceDerived.table,
                                    customerName: invoiceDerived.customerName,
                                    customer: invoiceDerived.customer,
                                    totalQty: invoiceDerived.totalQty,
                                    notes: invoiceDerived.notes,
                                    isLoadingItems: invoiceDerived.isLoadingItems,
                                }}
                            />
                        ) : (
                            <Spinner />
                        )}
                    </div>
                </div>
            </Drawer>
        </React.Fragment>
    );
}

function SummaryRow({ label, value }) {
    if (!value) return null;
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                padding: "0.35rem 0",
                borderBottom: "1px dashed #e5e9f2",
            }}
        >
            <div style={{ color: "#6E7187", fontWeight: 800, fontSize: 12 }}>
                {label}
            </div>
            <div style={{ fontWeight: 800, fontSize: 13, textAlign: "right" }}>
                {value}
            </div>
        </div>
    );
}

function InvoiceDetails({ classes, parentEntries, total, summary }) {
    const [itemSearch, setItemSearch] = useState("");
    const filteredParentEntries = useMemo(() => {
        const q = String(itemSearch || "").trim().toLowerCase();
        if (!q) return Array.isArray(parentEntries) ? parentEntries : [];
        const list = Array.isArray(parentEntries) ? parentEntries : [];

        const matchesEntry = (e) => {
            if (!e) return false;
            const hay = [
                e.item_name,
                e.item_code,
                e.notes,
                ...(Array.isArray(e.addons)
                    ? e.addons.flatMap((a) => [a?.item_name, a?.item_code, a?.notes])
                    : []),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(q);
        };

        return list.filter(matchesEntry);
    }, [parentEntries, itemSearch]);

    return (
        <div>
            {/* Summary */}
            <div
                style={{
                    background: "#fff",
                    border: "1px solid #e5e9f2",
                    borderRadius: 12,
                    padding: "0.75rem",
                    marginBottom: "0.75rem",
                }}
            >
                <SummaryRow label="Table" value={summary?.table} />
                <SummaryRow
                    label="Customer"
                    value={summary?.customerName || summary?.customer}
                />
                <SummaryRow
                    label="Total items"
                    value={summary?.totalQty ? String(summary.totalQty) : ""}
                />
                <SummaryRow
                    label="Total"
                    value={Number.isFinite(total) ? String(total) : ""}
                />
                {summary?.notes ? (
                    <div style={{ paddingTop: "0.5rem" }}>
                        <div
                            style={{
                                color: "#6E7187",
                                fontWeight: 900,
                                fontSize: 12,
                                marginBottom: "0.25rem",
                            }}
                        >
                            Notes
                        </div>
                        <div
                            style={{
                                background: "rgba(251, 253, 245, 0.75)",
                                border: "1px solid #e5e9f2",
                                borderRadius: 10,
                                padding: "0.6rem",
                                fontWeight: 700,
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {summary.notes}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Items */}
            <div className={classes.OrderWidget}>
                {Array.isArray(parentEntries) && parentEntries.length > 5 ? (
                    <div style={{ padding: "0 0.5rem 0.5rem 0.5rem" }}>
                        <TextField
                            size="small"
                            placeholder="Search items…"
                            value={itemSearch}
                            onChange={(e) => setItemSearch(e.target.value)}
                            fullWidth
                        />
                    </div>
                ) : null}
                <div className={classes.OrderCartItems}>
                    {summary?.isLoadingItems ? (
                        <div style={{ padding: "0.5rem", color: "#6E7187" }}>
                            Loading items…
                        </div>
                    ) : null}
                    {Array.isArray(filteredParentEntries) &&
                    filteredParentEntries.length > 0 ? (
                        filteredParentEntries.map((entry) => (
                            <ClosedOrderItemRow key={entry.id} entry={entry} />
                        ))
                    ) : (
                        !summary?.isLoadingItems ? (
                            <div style={{ padding: "0.5rem", color: "#6E7187" }}>
                                {itemSearch ? "No matching items" : "No items"}
                            </div>
                        ) : null
                    )}
                </div>
            </div>
        </div>
    );
}
export default ClosedOrders;
